import type { Transform } from '@spine-workspace/shared';

/**
 * Compose a child's LOCAL transform with a parent's WORLD transform to produce
 * the child's WORLD transform. Equivalent to: world = parentWorld × childLocal.
 *
 * All rotation values are in DEGREES (matching layer.transform.rotation).
 */
export function composeTransforms(childLocal: Transform, parentWorld: Transform): Transform {
  const pr = parentWorld.rotation * (Math.PI / 180);
  const cos = Math.cos(pr);
  const sin = Math.sin(pr);

  return {
    x: parentWorld.x + cos * childLocal.x * parentWorld.scaleX - sin * childLocal.y * parentWorld.scaleY,
    y: parentWorld.y + sin * childLocal.x * parentWorld.scaleX + cos * childLocal.y * parentWorld.scaleY,
    scaleX: parentWorld.scaleX * childLocal.scaleX,
    scaleY: parentWorld.scaleY * childLocal.scaleY,
    rotation: parentWorld.rotation + childLocal.rotation,
  };
}

/**
 * Convert a child's WORLD transform into its LOCAL transform relative to a parent WORLD transform.
 * This is the inverse of composeTransforms.
 *
 * All rotation values are in DEGREES.
 */
export function worldToLocal(childWorld: Transform, parentWorld: Transform): Transform {
  const pr = parentWorld.rotation * (Math.PI / 180);
  const cos = Math.cos(pr);
  const sin = Math.sin(pr);

  const dx = childWorld.x - parentWorld.x;
  const dy = childWorld.y - parentWorld.y;

  // Guard against zero scale
  const sx = parentWorld.scaleX || 1;
  const sy = parentWorld.scaleY || 1;

  return {
    x: ( cos * dx + sin * dy) / sx,
    y: (-sin * dx + cos * dy) / sy,
    scaleX: childWorld.scaleX / sx,
    scaleY: childWorld.scaleY / sy,
    rotation: childWorld.rotation - parentWorld.rotation,
  };
}

/**
 * Walk up the attachment chain to get a layer's WORLD transform.
 * Handles multi-level nesting.
 */
export function getLayerWorldTransform(
  layerId: string,
  layers: { id: string; transform: Transform; attachment: { parentLayerId: string | null } }[]
): Transform {
  const layer = layers.find((l) => l.id === layerId);
  if (!layer) return { x: 0, y: 0, scaleX: 1, scaleY: 1, rotation: 0 };
  if (!layer.attachment.parentLayerId) return layer.transform;

  const parentWorld = getLayerWorldTransform(layer.attachment.parentLayerId, layers);
  return composeTransforms(layer.transform, parentWorld);
}
